import type { EventDetail } from "@cdt/shared";
import { Flag, MailOpen, Shield, Star, UserCheck } from "lucide-react";

import { StatusBadge } from "@/components/events/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { StarRating } from "@/components/ui/StarRating";
import { useMarkEventUnseen } from "@/hooks/useMarkEventSeen";
import { useSetEventPriority } from "@/hooks/useSetEventPriority";
import { formatConfidence, formatDateTimeLong } from "@/lib/formatters";

interface EventDetailHeaderProps {
  event: EventDetail;
}

export function EventDetailHeader({ event }: EventDetailHeaderProps) {
  const markUnseen = useMarkEventUnseen();
  const priorityMutation = useSetEventPriority();

  return (
    <CardHeader className="space-y-3">
      {/* Title — full width on mobile, side-by-side on desktop */}
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
        <CardTitle className="text-xl md:text-2xl">
          {event.emailSubject}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <StarRating
            value={event.priority as 0 | 1 | 2 | 3}
            onChange={(p) => priorityMutation.mutate({ id: event.id, priority: p })}
            disabled={priorityMutation.isPending}
          />
          {event.isImportant && (
            <Badge
              variant="outline"
              className="gap-1 border-rose-300 bg-rose-50 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300"
              title="Email da mittente importante"
            >
              <Flag className="h-3 w-3" />
              Importante
            </Badge>
          )}
          {event.stage1Verdict === "FILTERED" && (
            <Badge
              variant="secondary"
              className="gap-1 text-xs"
              title="Scartata dal pre-filtro senza chiamate AI"
            >
              <Shield className="h-3 w-3" />
              Filtrata
            </Badge>
          )}
          <StatusBadge status={event.status} />
          {event.seenAt !== null && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
              disabled={markUnseen.isPending}
              onClick={() => markUnseen.mutate(event.id)}
              title="Segna come non letto"
            >
              <MailOpen className="h-3.5 w-3.5" />
              Non letto
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span>
          <span className="font-medium">Da:</span> {event.emailFrom}
        </span>
        <span aria-hidden>·</span>
        <span>
          <span className="font-medium">Ricevuta:</span>{" "}
          {formatDateTimeLong(event.emailReceivedAt)}
        </span>
        <span aria-hidden>·</span>
        <span>
          <span className="font-medium">Confidence:</span>{" "}
          {formatConfidence(event.confidence)}
        </span>
      </div>

      {/* Attribution row */}
      {(event.prioritySetBy ?? event.reviewedBy) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground border-t pt-2">
          {event.prioritySetBy && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              <span className="font-medium">Priorità impostata da</span>{" "}
              {event.prioritySetBy.name}
              {event.prioritySetAt && (
                <> il {formatDateTimeLong(event.prioritySetAt)}</>
              )}
            </span>
          )}
          {event.prioritySetBy && event.reviewedBy && (
            <span aria-hidden>·</span>
          )}
          {event.reviewedBy && (
            <span className="flex items-center gap-1">
              <UserCheck className="h-3 w-3 text-green-600" />
              <span className="font-medium">Approvato da</span>{" "}
              {event.reviewedBy.name}
              {event.reviewedAt && (
                <> il {formatDateTimeLong(event.reviewedAt)}</>
              )}
            </span>
          )}
        </div>
      )}
    </CardHeader>
  );
}
